// ─────────────────────────────────────────────────────────────
// AuthScreens.jsx
// Pantallas de login y registro normales (flujo sin join).
// Se muestran cuando screen === "login" o screen === "register"
// y el usuario no está en modo join.
// ─────────────────────────────────────────────────────────────

import { t } from "../i18n/translations";
import { initialLetter } from "../utils/idUtils";

// ─────────────────────────────────────────────────────────────
// LoginScreen
// Formulario de inicio de sesión con email y contraseña.
// ─────────────────────────────────────────────────────────────
export function LoginScreen({
  landingLang,
  loginForm, setLoginForm,
  authError,
  cameFromShared,
  handleLogin,
  handleForgotPassword,
  setSharedMode, setCameFromShared, setAuthError,
  setScreen,
}) {
  return (
    <div style={{ maxWidth: 420, margin: "0 auto" }}>
      {/* Botón volver si venía de modo compartido */}
      {cameFromShared && (
        <button
          className="btn btn-gris btn-sm"
          style={{ marginBottom: 16 }}
          onClick={() => { setSharedMode(true); setCameFromShared(false); setAuthError(""); }}
        >
          ← {t(landingLang, "back")}
        </button>
      )}

      <h2 className="seccion-titulo">{t(landingLang, "welcomeBack")}</h2>
      <p className="seccion-subtitulo">{t(landingLang, "signInToEdit")}</p>

      <div className="card">
        {/* Email */}
        <div className="campo">
          <label className="campo-label">{t(landingLang, "email")}</label>
          <input
            className="campo-input"
            type="email"
            placeholder="you@email.com"
            value={loginForm.email}
            onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>

        {/* Contraseña */}
        <div className="campo">
          <label className="campo-label">{t(landingLang, "password")}</label>
          <input
            className="campo-input"
            type="password"
            placeholder="••••••"
            value={loginForm.password}
            onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
          />
        </div>

        {/* Error */}
        {authError && (
          <p style={{ color: "#c0392b", fontSize: 13, marginBottom: 12, background: "#fff0f0", border: "1px solid #ffd0d0", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            ⚠️ {authError}
          </p>
        )}

        <button className="btn btn-negro btn-full" onClick={handleLogin}>
          {t(landingLang, "signIn")}
        </button>

        {/* Olvidé mi contraseña */}
        <div style={{ textAlign: "center", marginTop: 10, marginBottom: 4 }}>
          <span
            onClick={handleForgotPassword}
            style={{ fontSize: 12, color: "#d4a853", cursor: "pointer", textDecoration: "underline" }}
          >
            {landingLang === "es" ? "¿Olvidaste tu contraseña?" :
             landingLang === "fi" ? "Unohditko salasanasi?" :
             "Forgot your password?"}
          </span>
        </div>

        <div className="separador">
          {landingLang === "es" ? "— o —" : landingLang === "fi" ? "— tai —" : "— or —"}
        </div>

        <button
          className="btn btn-gris btn-full"
          onClick={() => { setScreen("register"); setAuthError(""); }}
        >
          {t(landingLang, "createAccount")}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// RegisterScreen
// Formulario de registro con nombre, email, contraseña,
// color de perfil, cumpleaños y aceptación de términos.
// ─────────────────────────────────────────────────────────────
export function RegisterScreen({
  landingLang,
  registerForm, setRegisterForm,
  authError,
  termsAccepted, setTermsAccepted,
  cameFromShared,
  handleRegister,
  setSharedMode, setCameFromShared, setAuthError,
  setScreen, setShowTermsModal,
}) {
  return (
    <div style={{ maxWidth: 420, margin: "0 auto" }}>
      {/* Botón volver si venía de modo compartido */}
      {cameFromShared && (
        <button
          className="btn btn-gris btn-sm"
          style={{ marginBottom: 16 }}
          onClick={() => { setSharedMode(true); setCameFromShared(false); setAuthError(""); }}
        >
          ← {t(landingLang, "back")}
        </button>
      )}

      <h2 className="seccion-titulo">{t(landingLang, "joinFamily")}</h2>
      <p className="seccion-subtitulo">{t(landingLang, "createWishlist")}</p>

      <div className="card">
        {/* Nombre */}
        <div className="campo">
          <label className="campo-label">{t(registerForm.language, "yourName")}</label>
          <input
            className="campo-input"
            placeholder={t(registerForm.language, "namePlaceholder")}
            value={registerForm.name}
            onChange={(e) => setRegisterForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>

        {/* Email */}
        <div className="campo">
          <label className="campo-label">{t(registerForm.language, "email")}</label>
          <input
            className="campo-input"
            type="email"
            placeholder="you@email.com"
            value={registerForm.email}
            onChange={(e) => setRegisterForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>

        {/* Contraseña */}
        <div className="campo">
          <label className="campo-label">{t(registerForm.language, "password")}</label>
          <input
            className="campo-input"
            type="password"
            placeholder={t(registerForm.language, "atLeast6")}
            value={registerForm.password}
            onChange={(e) => setRegisterForm((f) => ({ ...f, password: e.target.value }))}
          />
        </div>

        {/* Color de perfil */}
        <div className="campo">
          <label className="campo-label">{t(registerForm.language, "profileColor")}</label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <input
              type="color"
              value={registerForm.color}
              onChange={(e) => setRegisterForm((f) => ({ ...f, color: e.target.value }))}
              style={{ width: 48, height: 48, border: "none", borderRadius: 10, cursor: "pointer" }}
            />
            <div
              className="miembro-avatar"
              style={{ background: registerForm.color, width: 48, height: 48, fontSize: 20, margin: 0 }}
            >
              {initialLetter(registerForm.name)}
            </div>
            <span style={{ fontSize: 13, color: "#999" }}>{t(registerForm.language, "preview")}</span>
          </div>
        </div>

        {/* Cumpleaños */}
        <div className="campo">
          <label className="campo-label">{t(registerForm.language, "birthday")}</label>
          <input
            className="campo-input"
            placeholder="DD/MM/YYYY"
            value={registerForm.birthday}
            maxLength={10}
            onChange={(e) => {
              let v = e.target.value.replace(/\D/g, "");
              if (v.length >= 3 && v.length <= 4) v = v.slice(0, 2) + "/" + v.slice(2);
              else if (v.length >= 5) v = v.slice(0, 2) + "/" + v.slice(2, 4) + "/" + v.slice(4, 8);
              setRegisterForm((f) => ({ ...f, birthday: v }));
            }}
          />
        </div>

        {/* Error */}
        {authError && (
          <p style={{ color: "#c0392b", fontSize: 13, marginBottom: 12, background: "#fff0f0", border: "1px solid #ffd0d0", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            ⚠️ {authError}
          </p>
        )}

        {/* Términos y condiciones */}
        <div
          style={{ display: "flex", alignItems: "flex-start", gap: 10, margin: "12px 0", cursor: "pointer" }}
          onClick={() => setTermsAccepted((v) => !v)}
        >
          <div style={{
            width: 20, height: 20, borderRadius: 5,
            border: "2px solid " + (termsAccepted ? "#d4a853" : "#e0dbd2"),
            background: termsAccepted ? "#d4a853" : "transparent",
            flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1,
          }}>
            {termsAccepted && <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>}
          </div>
          <span style={{ fontSize: 12, color: "#999", lineHeight: 1.5 }}>
            {landingLang === "es" ? "Acepto los " : landingLang === "fi" ? "Hyväksyn " : "I accept the "}
            <span
              style={{ color: "#d4a853", textDecoration: "underline" }}
              onClick={(e) => { e.stopPropagation(); setShowTermsModal(true); }}
            >
              {landingLang === "es" ? "Términos y Condiciones" : landingLang === "fi" ? "Käyttöehdot" : "Terms and Conditions"}
            </span>
            {landingLang === "es" ? " y la " : landingLang === "fi" ? " ja " : " and "}
            <span
              style={{ color: "#d4a853", textDecoration: "underline" }}
              onClick={(e) => { e.stopPropagation(); setShowTermsModal(true); }}
            >
              {landingLang === "es" ? "Política de Privacidad" : landingLang === "fi" ? "Tietosuojakäytäntö" : "Privacy Policy"}
            </span>
          </span>
        </div>

        <button
          className="btn btn-dorado btn-full"
          onClick={handleRegister}
          disabled={!termsAccepted}
          style={{ opacity: termsAccepted ? 1 : 0.5 }}
        >
          {t(landingLang, "signUp")}
        </button>

        <div className="separador">— or —</div>

        <button
          className="btn btn-gris btn-full"
          onClick={() => { setScreen("login"); setAuthError(""); }}
        >
          {t(registerForm.language, "alreadyHaveAccount")}
        </button>
      </div>
    </div>
  );
}
